const QuickViewCache = {
  html: new Map(),
  inflight: new Map(),
};

function getQuickViewCacheKey(productUrl, sectionId) {
  return `${String(productUrl || '').split('?')[0]}::${sectionId}`;
}

function extractQuickViewTemplateHtml(responseText) {
  const doc = new DOMParser().parseFromString(responseText, 'text/html');
  const template = doc.querySelector('#MainProduct-quick-view__content');
  return template ? template.innerHTML : '';
}

function preloadQuickViewHeroImage(url) {
  if (!url || document.querySelector(`link[rel="preload"][href="${url}"]`)) return;
  const link = document.createElement('link');
  link.rel = 'preload';
  link.as = 'image';
  link.href = url;
  document.head.appendChild(link);
}

function fetchQuickViewHtml(productUrl, sectionId, priority = 'low') {
  const key = getQuickViewCacheKey(productUrl, sectionId);
  if (QuickViewCache.html.has(key)) {
    return Promise.resolve(QuickViewCache.html.get(key));
  }
  if (QuickViewCache.inflight.has(key)) {
    return QuickViewCache.inflight.get(key);
  }

  const sectionUrl = `${String(productUrl).split('?')[0]}?section_id=${sectionId}`;
  const promise = fetch(sectionUrl, { priority })
    .then((response) => response.text())
    .then((responseText) => {
      const html = extractQuickViewTemplateHtml(responseText);
      QuickViewCache.html.set(key, html);
      QuickViewCache.inflight.delete(key);
      return html;
    })
    .catch((error) => {
      QuickViewCache.inflight.delete(key);
      throw error;
    });

  QuickViewCache.inflight.set(key, promise);
  return promise;
}

function getProductHandleFromUrl(productUrl) {
  const match = String(productUrl || '').match(/\/products\/([^/?#]+)/);
  return match ? decodeURIComponent(match[1]) : null;
}

function isCorporatePage() {
  const cls = document.body.classList;
  return (
    cls.contains('corporate-store') ||
    cls.contains('corporate-new') ||
    cls.contains('corporate-order-details') ||
    cls.contains('corporate-order-confirmation')
  );
}

function tryOpenBundleAppProductDetail(productUrl) {
  // Corporate gifting is a one-time Shopify draft-order checkout. Opening the
  // subscription box builder from a gift's "What's inside" list would let
  // someone add consumer SKUs to a regular box.
  if (isCorporatePage()) return false;

  // Only when the new bundle app (portal variant) is actually mounted on the
  // page — control-variant shoppers and pages without the app keep the theme
  // quick-view drawer.
  if (!document.querySelector('.wildgrain-box-builder-root [data-portal="true"]')) return false;

  const handle = getProductHandleFromUrl(productUrl);
  if (!handle) return false;
  // Pages can opt in to the app modal for EVERY product (the collection grid
  // section renders data-wg-prefer-app-modal when its setting is on). With
  // force, the app also claims products outside its box/add-on catalog and
  // shows them browse-only; without it, unknown products fall back to the
  // theme drawer below.
  const force = !!document.querySelector('[data-wg-prefer-app-modal]');
  // The bundle app's box-builder portal stays mounted (hidden) on storefront
  // pages and listens for this event. It preventDefaults when it recognizes
  // the product (or when forced) and opens its own detail modal; otherwise we
  // fall back to the theme's quick-view drawer.
  const event = new CustomEvent('wg:open-product-detail', {
    detail: { handle, force },
    cancelable: true,
  });
  return !window.dispatchEvent(event);
}

function findQuickViewModalFromEvent(event, priority = 'low') {
  const start = event.target;
  if (!start || !start.closest) return null;

  const card = start.closest('[data-quick-view-modal], [data-modal-trigger]');
  const modalId = card?.dataset.quickViewModal || card?.dataset.modalTrigger;
  if (modalId) {
    const modal = document.getElementById(modalId);
    if (modal?.tagName === 'QUICK-VIEW-MODAL') return modal;
  }

  const trigger = start.closest('[aria-controls], [data-quick-view-trigger]');
  if (trigger) {
    const id = trigger.getAttribute('aria-controls') || trigger.dataset.quickViewTrigger;
    const modal = id ? document.getElementById(id) : null;
    if (modal?.tagName === 'QUICK-VIEW-MODAL') return modal;
  }

  const bundleCard = start.closest('[data-product-handle]');
  if (bundleCard?.dataset.productHandle) {
    const section = bundleCard.closest('.shopify-section');
    const modal =
      section?.querySelector('quick-view-modal[data-bundle-preview]') ||
      section?.querySelector('quick-view-modal[id^="BundleProductsQuickView"]') ||
      document.querySelector('quick-view-modal[data-bundle-preview]');
    if (modal?.tagName === 'QUICK-VIEW-MODAL') {
      prefetchQuickViewUrl(`/products/${bundleCard.dataset.productHandle}`, modal, priority);
    }
  }

  return null;
}

function getSharedQuickViewSectionId(modal) {
  if (typeof modal.getProductQuickViewSectionId === 'function') {
    return modal.getProductQuickViewSectionId();
  }
  if (window.FoxTheme?.QuickViewSectionId) return FoxTheme.QuickViewSectionId;
  const productQuickView = document.querySelector('.section-group-overlay-quick-view');
  if (productQuickView && window.FoxTheme?.utils?.getSectionId) {
    FoxTheme.QuickViewSectionId = FoxTheme.utils.getSectionId(productQuickView);
    return FoxTheme.QuickViewSectionId;
  }
  return null;
}

function prefetchQuickViewUrl(productUrl, modal, priority = 'low') {
  if (!productUrl || !modal) return;
  const sectionId = getSharedQuickViewSectionId(modal);
  if (!sectionId) return;
  fetchQuickViewHtml(productUrl, sectionId, priority);
}

function prefetchQuickViewModal(modal, priority = 'low') {
  if (!modal?.dataset.productUrl) return;

  if (modal.dataset.heroImage) {
    preloadQuickViewHeroImage(modal.dataset.heroImage);
  }

  prefetchQuickViewUrl(modal.dataset.productUrl, modal, priority);
}

function initQuickViewPrefetch() {
  const handler = (event) => {
    const priority = event.type === 'pointerdown' ? 'high' : 'low';
    const modal = findQuickViewModalFromEvent(event, priority);
    if (modal) prefetchQuickViewModal(modal, priority);

    // Seed the bundle app's hero fly-in animation: it reads
    // window.__wgDetailOrigin (card image rect + src) when its detail modal
    // opens within 800ms of this pointerdown.
    if (event.type === 'pointerdown' && event.target.closest) {
      const card = event.target.closest(
        '[data-quick-view-modal], [data-modal-trigger], [data-product-handle], [data-quick-view-trigger]'
      );
      const img = card ? card.querySelector('img') : null;
      const src = img && (img.currentSrc || img.src);
      if (src) {
        window.__wgDetailOrigin = { rect: img.getBoundingClientRect(), src, at: Date.now() };
      }
    }
  };

  document.addEventListener('pointerenter', handler, { capture: true, passive: true });
  document.addEventListener('pointerdown', handler, { capture: true, passive: true });
  document.addEventListener('focusin', handler, { capture: true, passive: true });
}

initQuickViewPrefetch();

if (!customElements.get('quick-view-modal')) {
  customElements.define(
    'quick-view-modal',
    class QuickViewModal extends DrawerComponent {
      constructor() {
        super();

        this._classes = {
          loaded: 'quick-view-loaded',
        };
        this.drawerBody = this.querySelector(this.selector);
        this._bundleCache = {};
        this._bundleReturnUrl = null;
        this._renderedUrl = null;
        this._requestId = 0;
      }

      get isLockingNeeded() {
        return false; // We handle scroll locking manually to ensure it works
      }

      get selector() {
        return '.quick-view__content';
      }

      get sourceSelector() {
        return '#MainProduct-quick-view__content';
      }

      get requiresBodyAppended() {
        return !(Shopify.designMode && this.closest('.section-group-overlay-quick-view'));
      }

      shouleBeShow() {
        const sectionId = this.getProductQuickViewSectionId();
        return typeof sectionId === 'string';
      }

      show(activeElement = null, animate = true) {
        // Prefer the bundle app's product detail modal when it can handle this
        // product; only fall back to the theme drawer when it can't.
        // Read-only drawers (corporate "What's inside") must not hijack into
        // the box builder — that path has add-to-box controls.
        if (
          !this.classList.contains('quick-view--readonly') &&
          !(window.Shopify && Shopify.designMode) &&
          tryOpenBundleAppProductDetail(this.dataset.productUrl)
        ) {
          return;
        }

        // Prevent body scroll without layout shift by preserving scrollbar space
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
        document.body.style.overflow = 'hidden';
        document.body.classList.add('quick-view-modal-open');
        if (scrollbarWidth > 0) {
          document.body.style.paddingRight = `${scrollbarWidth}px`;
        }
        
        // Also lock html element
        document.documentElement.style.overflow = 'hidden';
        
        // Call parent show method
        const result = super.show(activeElement, animate);

        // If the parent refused to open (e.g. no quick-view section id), the
        // lock above would be stranded with nothing to ever release it.
        if (!this.open) {
          this.restoreScrollLock();
        }

        return result;
      }

      restoreScrollLock() {
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
        document.body.classList.remove('quick-view-modal-open');
        document.documentElement.style.overflow = '';
      }

      hide() {
        // Call parent hide method first
        const result = super.hide();
        
        // Restore body scroll after transition completes
        const overlay = this.querySelector('.fixed-overlay');
        let restored = false;
        const restoreScroll = () => {
          if (restored) return;
          restored = true;
          this.restoreScrollLock();
        };

        if (overlay) {
          overlay.addEventListener('transitionend', restoreScroll, { once: true });
          overlay.addEventListener('transitioncancel', restoreScroll, { once: true });
        }
        // Safety fallback — transitionend never fires when the overlay had no
        // transition to run (drawer closed before it became active, reduced
        // motion, mid-transition DOM moves). Without this the page is left
        // permanently unscrollable, which mobile users hit after closing the
        // cart drawer that opened on top of a quick view.
        setTimeout(restoreScroll, 800);
        
        return result;
      }

      handleShowTransition() {
        requestAnimationFrame(() => {
          this.setAttribute('active', '');
        });

        return new Promise((resolve) => {
          const overlay = this.overlay;
          if (!overlay) {
            resolve();
            return;
          }
          overlay.addEventListener('transitionend', resolve, { once: true });
        });
      }

      prepareToShow() {
        super.prepareToShow();
        this.classList.remove(this._classes.loaded);
        const spinner = this.querySelector('.quick-view__spinner');
        if (spinner) spinner.hidden = false;
        if (this.dataset.heroImage) {
          preloadQuickViewHeroImage(this.dataset.heroImage);
        }
        this.quickview();
      }

      handleAfterShow() {
        super.handleAfterShow();

        // Closed again before the open transition settled — re-applying the
        // lock now would strand it, since hide()'s restore already ran.
        if (!this.open) return;

        // Ensure scroll lock is still applied (in case parent removed it)
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
        document.body.style.overflow = 'hidden';
        document.body.classList.add('quick-view-modal-open');
        document.documentElement.style.overflow = 'hidden';
        if (scrollbarWidth > 0) {
          document.body.style.paddingRight = `${scrollbarWidth}px`;
        }
        
        document.dispatchEvent(
          new CustomEvent('quick-view:open', {
            detail: { productUrl: this.dataset.productUrl },
          })
        );
      }

      handleAfterHide() {
        super.handleAfterHide();
        // Idempotent; also covers the drawer being removed from the DOM while
        // open (disconnectedCallback), where hide()'s listeners never run.
        this.restoreScrollLock();
        const drawerContent = this.querySelector(this.selector);
        if (drawerContent) drawerContent.innerHTML = '';
        this.classList.remove(this._classes.loaded);
        this._renderedUrl = null;
        this.removeBackButton();
        if (this._bundleReturnUrl) {
          this.dataset.productUrl = this._bundleReturnUrl;
          this._bundleReturnUrl = null;
        }
      }

      getProductQuickViewSectionId() {
        let sectionId = FoxTheme.QuickViewSectionId || false;

        if (!sectionId) {
          // Get section id from overlay groups.
          const productQuickView = document.querySelector('.section-group-overlay-quick-view');
          if (productQuickView) {
            sectionId = FoxTheme.utils.getSectionId(productQuickView);
          }

          // Cache for better performance.
          FoxTheme.QuickViewSectionId = sectionId;
        }

        return sectionId;
      }

      quickview() {
        const productUrl = this.dataset.productUrl;
        const sectionId = this.getProductQuickViewSectionId();
        if (!productUrl || !sectionId) return;

        const requestId = ++this._requestId;

        fetchQuickViewHtml(productUrl, sectionId, 'high')
          .then((html) => {
            if (requestId !== this._requestId) return;
            if (this.dataset.productUrl !== productUrl) return;
            if (!html) return;
            this.scheduleRender(html, productUrl, requestId);
          })
          .catch((e) => {
            console.error(e);
          });
      }

      scheduleRender(html, productUrl, requestId) {
        const startedAt = performance.now();
        const attempt = () => {
          if (requestId !== this._requestId) return;
          if (!this.hasAttribute('open')) return;
          const drawerIsActive = this.hasAttribute('active') || performance.now() - startedAt > 200;
          if (!drawerIsActive) {
            requestAnimationFrame(attempt);
            return;
          }
          requestAnimationFrame(() => {
            setTimeout(() => {
              if (requestId !== this._requestId) return;
              if (!this.hasAttribute('open')) return;
              this.renderQuickView(html, productUrl);
            }, 0);
          });
        };
        attempt();
      }

      renderQuickView(html, productUrl) {
        const drawerContent = this.querySelector(this.selector);
        if (!drawerContent) return;

        const template = document.createElement('template');
        template.innerHTML = html;
        this.setInnerHTML(drawerContent, template.content.cloneNode(true));
        this._renderedUrl = productUrl.split('?')[0];

        if (this._bundleReturnUrl) {
          this.insertBackButton(drawerContent);
        }

        FoxTheme.a11y.trapFocus(this, this.focusElement);

        if (window.Shopify && Shopify.PaymentButton) {
          Shopify.PaymentButton.init();
        }

        this.classList.add(this._classes.loaded);

        document.dispatchEvent(
          new CustomEvent('quick-view:loaded', {
            detail: { productUrl },
          })
        );

        this.loadBundleContents(drawerContent);
      }

      navigateToBundleItem(handle) {
        this._bundleReturnUrl = this.dataset.productUrl;
        this.dataset.productUrl = `/products/${handle}`;
        const content = this.querySelector(this.selector);
        content.innerHTML = '';
        this.classList.remove(this._classes.loaded);
        this._renderedUrl = null;
        this.quickview();
      }

      navigateBackToBundle() {
        if (!this._bundleReturnUrl) return;
        this.dataset.productUrl = this._bundleReturnUrl;
        this._bundleReturnUrl = null;
        this.removeBackButton();
        const content = this.querySelector(this.selector);
        content.innerHTML = '';
        this.classList.remove(this._classes.loaded);
        this._renderedUrl = null;
        this.quickview();
      }

      removeBackButton() {
        const existing = this.querySelector('.bundle-back-btn');
        if (existing) existing.remove();
      }

      ensureBundleCssLoaded() {
        if (document.querySelector('link[href*="bundle-contents.css"]')) return;
        const base = this.getAssetBaseUrl();
        if (!base) return;
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = `${base}bundle-contents.css`;
        document.head.appendChild(link);
      }

      insertBackButton(drawerContent) {
        this.ensureBundleCssLoaded();

        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'bundle-back-btn';
        Object.assign(btn.style, {
          position: 'absolute',
          top: '2rem',
          right: 'calc(2rem + var(--drawer-close-btn-size, 4.8rem) + 0.8rem)',
          height: 'var(--drawer-close-btn-size, 4.8rem)',
          zIndex: '9',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.35rem',
          padding: '0 1.2rem',
          border: '0.1rem solid rgb(var(--color-border))',
          borderRadius: '2.4rem',
          background: 'rgb(var(--color-background))',
          color: 'rgb(var(--color-foreground), 0.75)',
          fontSize: '1.2rem',
          fontFamily: 'inherit',
          cursor: 'pointer',
        });
        btn.innerHTML = `
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M10 3L5 8L10 13" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <span>Back to bundle</span>
        `;
        btn.addEventListener('click', () => this.navigateBackToBundle());

        const closeBtn = this.querySelector('.drawer__close-btn');
        if (closeBtn) {
          closeBtn.insertAdjacentElement('afterend', btn);
        } else {
          drawerContent.prepend(btn);
        }
      }

      getStorefrontToken() {
        const el = document.querySelector('products-bundle[data-storefront-token]') ||
          document.querySelector('bundle-contents[data-storefront-token]');
        return el?.dataset.storefrontToken || '94fa5e84d61642557dfe538209b86a4d';
      }

      getShopDomain() {
        const el = document.querySelector('products-bundle[data-shop-domain]') ||
          document.querySelector('bundle-contents[data-shop-domain]');
        return el?.dataset.shopDomain || window.Shopify?.shop || location.hostname;
      }

      getAssetBaseUrl() {
        const link = document.querySelector('link[href*="/assets/"]');
        if (link) {
          const href = link.getAttribute('href');
          const idx = href.indexOf('/assets/');
          if (idx !== -1) return href.substring(0, idx + '/assets/'.length);
        }
        return null;
      }

      isLikelyBundleSku(sku) {
        const value = String(sku || '').toUpperCase();
        return value.includes('WGP') || value.startsWith('WGB') || value.includes('_BOX');
      }

      async loadBundleContents(drawerContent) {
        const productUrl = this.dataset.productUrl;
        if (!productUrl) return;

        const handle = productUrl.split('/products/')[1]?.split('?')[0]?.split('#')[0];
        if (!handle) return;

        const knownSku = this.dataset.variantSku || '';
        if (knownSku && !this.isLikelyBundleSku(knownSku) && !this._bundleReturnUrl) return;

        try {
          const productRes = await fetch(`/products/${handle}.json`);
          if (!productRes.ok) return;

          const { product } = await productRes.json();
          if (!product) return;

          const variantInput = drawerContent.querySelector('input[name="id"]');
          let variantId = variantInput?.value;
          let variantSku = '';

          if (variantId && product.variants) {
            const selected = product.variants.find(
              (v) => String(v.id) === String(variantId)
            );
            variantSku = selected?.sku || '';
          }

          if (!variantId || !variantSku) {
            const first = product.variants?.[0];
            if (!first) return;
            variantId = variantId || String(first.id);
            variantSku = variantSku || first.sku || '';
          }

          if (!this.isLikelyBundleSku(variantSku)) return;

          const cacheKey = variantSku || variantId;
          if (this._bundleCache[cacheKey]) {
            this.renderBundleContents(drawerContent, this._bundleCache[cacheKey]);
            return;
          }

          const token = this.getStorefrontToken();
          const shopDomain = this.getShopDomain();
          const storefrontUrl = `https://${shopDomain}/api/2025-01/graphql.json`;

          let components = await this.fetchBundleFromWildgrainApi(variantSku);

          if (components?.length) {
            components = await this.enrichWithStorefrontImages(components, token, storefrontUrl);
          } else {
            components = await this.fetchBundleFromStorefront(variantId, token, storefrontUrl);
          }

          if (components?.length) {
            this._bundleCache[cacheKey] = components;
            this.renderBundleContents(drawerContent, components);
          }
        } catch (e) {
          /* silent */
        }
      }

      async fetchBundleFromWildgrainApi(sku) {
        try {
          const res = await fetch(
            `https://api.wildgrain.com/api/bundle/storefront/${encodeURIComponent(sku)}?box_number=1`
          );
          if (!res.ok) return null;

          const json = await res.json();
          if (!json.success || !json.items?.length) return null;

          return json.items.map((item) => ({
            quantity: item.quantity,
            variantShopifyId: item.variant_shopify_id,
            productName: item.product_name || item.sku,
            handle: item.handle || '',
          }));
        } catch {
          return null;
        }
      }

      async enrichWithStorefrontImages(items, token, storefrontUrl) {
        const ids = items.map((i) => i.variantShopifyId).filter(Boolean);
        if (!ids.length) {
          return items.map((i) => ({
            quantity: i.quantity,
            productVariant: {
              title: i.productName,
              image: null,
              product: { title: i.productName, handle: i.handle },
            },
          }));
        }

        try {
          const fragments = ids.map(
            (id, idx) =>
              `v${idx}: node(id: "gid://shopify/ProductVariant/${id}") {
                ... on ProductVariant { id title image { url altText } product { title handle } }
              }`
          );
          const res = await fetch(storefrontUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Shopify-Storefront-Access-Token': token,
            },
            body: JSON.stringify({ query: `{ ${fragments.join('\n')} }` }),
          });
          const json = await res.json();
          const data = json?.data || {};
          const map = {};
          Object.values(data).forEach((n) => {
            if (n?.id) map[n.id.replace('gid://shopify/ProductVariant/', '')] = n;
          });

          return items.map((i) => {
            const vd = map[i.variantShopifyId] || {};
            return {
              quantity: i.quantity,
              productVariant: {
                title: vd.title || i.productName,
                image: vd.image || null,
                product: {
                  title: vd.product?.title || i.productName,
                  handle: vd.product?.handle || i.handle,
                },
              },
            };
          });
        } catch {
          return items.map((i) => ({
            quantity: i.quantity,
            productVariant: {
              title: i.productName,
              image: null,
              product: { title: i.productName, handle: i.handle },
            },
          }));
        }
      }

      async fetchBundleFromStorefront(variantId, token, storefrontUrl) {
        const gid = `gid://shopify/ProductVariant/${variantId}`;
        const query = `query BundleComponents($id: ID!) {
          node(id: $id) {
            ... on ProductVariant {
              id
              requiresComponents
              components(first: 20) {
                nodes {
                  quantity
                  productVariant {
                    id title image { url altText }
                    product { title handle }
                    selectedOptions { name value }
                  }
                }
              }
            }
          }
        }`;

        try {
          const res = await fetch(storefrontUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Shopify-Storefront-Access-Token': token,
            },
            body: JSON.stringify({ query, variables: { id: gid } }),
          });
          const json = await res.json();
          const node = json?.data?.node;
          if (!node?.requiresComponents) return null;
          return node.components?.nodes ?? [];
        } catch {
          return null;
        }
      }

      renderBundleContents(drawerContent, components) {
        if (!components.length) return;

        this.ensureBundleCssLoaded();

        const listHtml = components
          .map((c) => {
            const pv = c.productVariant;
            const title = pv.product?.title ?? pv.title;
            const handle = pv.product?.handle ?? '';
            const imgUrl = pv.image?.url;
            const alt = (pv.image?.altText || title).replace(/"/g, '&quot;');

            return `<li class="bundle-contents__item"${
              handle ? ` data-handle="${handle}" role="button" tabindex="0"` : ''
            }>
              ${
                imgUrl
                  ? `<div class="bundle-contents__img">
                      <img src="${imgUrl}&width=120" alt="${alt}" width="60" height="60" loading="lazy">
                    </div>`
                  : ''
              }
              <div class="bundle-contents__details">
                <span class="bundle-contents__qty">${c.quantity} &times;</span>
                <span class="bundle-contents__title">${title}</span>
              </div>
            </li>`;
          })
          .join('');

        const section = document.createElement('div');
        section.className = 'bundle-contents product__block';
        section.innerHTML = `
          <div class="bundle-contents__header">
            <h3 class="bundle-contents__heading h3">What's in the Box</h3>
          </div>
          <ul class="bundle-contents__list" role="list">${listHtml}</ul>
        `;

        const descriptionBlock = drawerContent.querySelector('.product__block--description');
        if (descriptionBlock) {
          descriptionBlock.insertAdjacentElement('afterend', section);
        } else {
          const infoWrapper = drawerContent.querySelector('.product__info-wrapper');
          if (infoWrapper) {
            infoWrapper.appendChild(section);
          } else {
            drawerContent.appendChild(section);
          }
        }

        section.querySelectorAll('.bundle-contents__item[data-handle]').forEach((item) => {
          const navigate = () => {
            const h = item.dataset.handle;
            if (h) this.navigateToBundleItem(h);
          };
          item.addEventListener('click', navigate);
          item.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              navigate();
            }
          });
        });
      }

      setInnerHTML(element, innerHTML) {
        element.innerHTML = '';
        element.appendChild(innerHTML);
        element.querySelectorAll('script').forEach((oldScriptTag) => {
          const newScriptTag = document.createElement('script');
          Array.from(oldScriptTag.attributes).forEach((attribute) => {
            newScriptTag.setAttribute(attribute.name, attribute.value);
          });
          newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
          oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
        });
      }

      disconnectedCallback() {
        super.disconnectedCallback();
        this.handleAfterHide();
      }
    }
  );
}
